import DOMPurify from 'dompurify';

/** Allow https/mailto/cid and embedded data:image for signature logos pasted in the editor. */
export const RICH_EMAIL_HTML_ALLOWED_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

/** DOMPurify options aligned with typical HTML email (tables, inline styles, legacy tags). */
export const RICH_EMAIL_HTML_DOM_PURIFY: Parameters<typeof DOMPurify.sanitize>[1] = {
  ALLOWED_URI_REGEXP: RICH_EMAIL_HTML_ALLOWED_URI_REGEXP,
  ADD_TAGS: [
    'style',
    'link',
    'font',
    'center',
    'pre',
    's',
    'strike',
    'u',
    'tbody',
    'thead',
    'tfoot',
    'colgroup',
    'col',
  ],
  ADD_ATTR: [
    'style',
    'target',
    'rel',
    'media',
    'width',
    'height',
    'class',
    'border',
    'cellpadding',
    'cellspacing',
    'align',
    'valign',
    'bgcolor',
    'background',
    'color',
    'size',
    'face',
    'dir',
    'colspan',
    'rowspan',
    'scope',
    'abbr',
    'axis',
    'headers',
    'nowrap',
    'frame',
    'rules',
    'summary',
  ],
  RETURN_DOM_FRAGMENT: true,
};

/** DOMPurify typings often infer `string` even when `RETURN_DOM_FRAGMENT` yields a fragment. */
function domPurifyRichToInnerHtml(dirty: string): string {
  const purified: unknown = DOMPurify.sanitize(dirty, RICH_EMAIL_HTML_DOM_PURIFY);
  const outDiv = document.createElement('div');
  if (purified instanceof DocumentFragment) {
    outDiv.appendChild(purified);
  } else if (typeof purified === 'string') {
    outDiv.innerHTML = purified;
  }
  return outDiv.innerHTML;
}

/**
 * Sanitize custom/pasted HTML for signatures, reply intro, etc. Preserves layout and styling
 * like the main email body sanitizer (without cid: image rewriting).
 */
export function sanitizeRichEmailHtml(html: string): string {
  if (!html?.trim()) return '';
  return stripScripts(domPurifyRichToInnerHtml(html.trim()));
}

function stripScripts(html: string): string {
  if (!html) return html;
  let out = html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');
  out = out.replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, '');
  out = out.replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, '');
  out = out.replace(/<iframe\b[^>]*\/?>/gi, '');
  const div = document.createElement('div');
  div.innerHTML = out;
  div.querySelectorAll('*').forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (/^on\w+/i.test(attr.name) || (attr.name === 'formaction' && /^\s*javascript:/i.test(attr.value))) {
        el.removeAttribute(attr.name);
      }
    });
    const href = el.getAttribute('href');
    if (href && /^\s*javascript:/i.test(href)) el.removeAttribute('href');
  });
  return div.innerHTML;
}

/**
 * Sanitize email body HTML for safe display: rewrite cid: images, DOMPurify, strip scripts.
 * Use for iframe (EmailBodyView) or editable div (forwarded message).
 */
export function sanitizeEmailBodyForDisplay(
  bodyHtml: string,
  emailId: number,
  apiBaseUrl: string,
  partitionAt?: string | null
): string {
  if (!bodyHtml) return '';
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = bodyHtml;
  const imgTags = tempDiv.querySelectorAll('img');
  imgTags.forEach((img) => {
    const src = (img.getAttribute('src') || '').trim();
    let rawContentId = '';
    if (src.startsWith('cid:')) {
      rawContentId = src.substring(4).trim();
    } else if (src.toLowerCase().startsWith('cid%3a')) {
      try {
        rawContentId = decodeURIComponent(src.substring(7).trim());
      } catch {
        rawContentId = src.substring(7).trim();
      }
    }
    if (rawContentId) {
      rawContentId = rawContentId.replace(/[<>]/g, '');
      const encodedContentId = encodeURIComponent(rawContentId);
      const pa =
        partitionAt != null && partitionAt !== ''
          ? `?partitionAt=${encodeURIComponent(partitionAt)}`
          : '';
      img.setAttribute('src', `${apiBaseUrl}/emails/${emailId}/inline-image/${encodedContentId}${pa}`);
      if (!img.getAttribute('style')) {
        img.setAttribute('style', 'max-width:100%;height:auto');
      }
    }
  });
  const raw = tempDiv.innerHTML;
  return stripScripts(domPurifyRichToInnerHtml(raw));
}
